(() => {
  const enableDomReadyMotion = () => {
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add('is-dom-ready');
    });
  };

  const headers = document.querySelectorAll('[data-header]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initMobileMenu = (header) => {
    const toggle = header.querySelector('[data-mobile-menu-toggle]');
    const menu = header.querySelector('[data-mobile-menu]');
    const closeButton = header.querySelector('[data-mobile-menu-close]');
    const panels = menu ? [...menu.querySelectorAll('[data-mobile-menu-panel]')] : [];
    const rootPanel = menu?.querySelector('[data-mobile-menu-root]');
    const promoTemplate = menu?.querySelector('[data-mobile-menu-promo-template]');
    const mobileViewport = window.matchMedia('(max-width: 1199px)');

    if (!toggle || !menu || !closeButton || !rootPanel) return;

    let isMenuOpen = false;
    let activePanel = rootPanel;
    let promo = menu.querySelector('[data-mobile-menu-promo]');

    const syncMenuInputMode = (event) => {
      header.classList.toggle('is-pointer-menu-action', event.detail !== 0);
    };

    const getFocusableElements = () => {
      return [...menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')]
        .filter((element) => !element.closest('[hidden]'));
    };

    const mountPromoInRoot = () => {
      if (!promo && promoTemplate?.content.firstElementChild) {
        promo = promoTemplate.content.firstElementChild.cloneNode(true);
      }

      if (!promo) return;

      const slot = rootPanel.querySelector('[data-mobile-menu-promo-slot]');
      if (slot && promo.parentElement !== slot) {
        slot.append(promo);
      }
    };

    const showPanel = (panelId, shouldFocus = true) => {
      const nextPanel = panels.find((panel) => panel.id === panelId);
      if (!nextPanel) return;

      panels.forEach((panel) => {
        panel.hidden = panel !== nextPanel;
      });

      activePanel = nextPanel;
      activePanel.scrollTop = 0;
      if (activePanel === rootPanel) mountPromoInRoot();

      if (shouldFocus) {
        const focusTarget = activePanel.querySelector('.site-header__mobile-back, .site-header__mobile-nav-link');
        focusTarget?.focus({ preventScroll: true });
      }
    };

    const resetPanel = () => {
      showPanel(rootPanel.id, false);
    };

    const closeMenu = (returnFocus = true) => {
      if (!isMenuOpen) return;

      isMenuOpen = false;
      toggle.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      header.classList.remove('is-mobile-menu-open');
      document.documentElement.classList.remove('has-open-mobile-menu');
      resetPanel();

      if (returnFocus) {
        toggle.focus({ preventScroll: true });
      }
    };

    const openMenu = () => {
      if (isMenuOpen) return;

      isMenuOpen = true;
      toggle.setAttribute('aria-expanded', 'true');
      resetPanel();

      const headerTop = Math.max(header.getBoundingClientRect().top, 0);
      menu.style.setProperty('--mobile-menu-panel-top', `${Math.round(headerTop + 10)}px`);
      menu.hidden = false;
      header.classList.add('is-mobile-menu-open');
      document.documentElement.classList.add('has-open-mobile-menu');
      closeButton.focus({ preventScroll: true });
    };

    toggle.addEventListener('click', (event) => {
      syncMenuInputMode(event);
      if (isMenuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    closeButton.addEventListener('click', (event) => {
      syncMenuInputMode(event);
      closeMenu();
    });

    menu.addEventListener('click', (event) => {
      const panelTarget = event.target.closest('[data-mobile-menu-panel-target]');

      if (panelTarget) {
        showPanel(panelTarget.dataset.mobileMenuPanelTarget);
        return;
      }

      if (event.target.closest('a')) {
        closeMenu(false);
        return;
      }

      if (event.target === menu) {
        closeMenu();
      }
    });

    document.addEventListener('keydown', (event) => {
      header.classList.remove('is-pointer-menu-action');
      if (!isMenuOpen) return;

      if (event.key === 'Escape') {
        closeMenu();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (!firstFocusable || !lastFocusable) return;

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus({ preventScroll: true });
      }
    });

    mobileViewport.addEventListener('change', (event) => {
      if (!event.matches) {
        closeMenu(false);
      }
    });
  };

  const initDesktopSearch = (header) => {
    const search = header.querySelector('[data-desktop-search]');
    if (!search) return;

    const toggle = search.querySelector('[data-search-toggle]');
    const form = search.querySelector('[data-desktop-search-form]');
    const input = search.querySelector('[data-search-input]');
    const mobileInput = header.querySelector('[data-mobile-search-input]');
    const nav = header.querySelector('.site-header__nav');
    const isAlwaysOpen = search.dataset.searchAlwaysOpen === 'true';
    const desktop = window.matchMedia('(min-width: 1200px)');

    if (!toggle || !form || !input) return;

    const words = [
      search.dataset.placeholderWordOne,
      search.dataset.placeholderWordTwo,
      search.dataset.placeholderWordThree,
      search.dataset.placeholderWordFour,
    ].map((word) => (word || '').trim()).filter(Boolean);

    const typeDelay = 90;
    const eraseDelay = 35;
    const holdDelay = 1000;
    let placeholderTimer = null;
    let wordIndex = 0;
    let characterIndex = 0;
    let isErasing = false;
    let isTyping = false;
    let observer = null;
    let isSearchOpen = search.classList.contains('is-search-open');
    let isNavOverlapping = false;
    let isNavSyncTicking = false;

    const setPlaceholder = (value) => {
      input.setAttribute('placeholder', value);
      mobileInput?.setAttribute('placeholder', value);
    };
    const setNavOverlap = (isOverlapping) => {
      if (isOverlapping === isNavOverlapping) return;

      isNavOverlapping = isOverlapping;
      header.classList.toggle('is-desktop-search-overlapping-nav', isOverlapping);
    };
    const syncProjectedNavVisibility = () => {
      if (!desktop.matches || !nav) return;

      const navRect = nav.getBoundingClientRect();
      const searchRect = search.getBoundingClientRect();
      const projectedSearchWidth = Math.min(394, window.innerWidth - 48);
      const projectedSearchLeft = searchRect.right - projectedSearchWidth;

      setNavOverlap(projectedSearchLeft < navRect.right && searchRect.right > navRect.left);
    };
    const syncNavVisibility = () => {
      isNavSyncTicking = false;

      if (!isSearchOpen || !nav) {
        setNavOverlap(false);
        return;
      }

      const navRect = nav.getBoundingClientRect();
      const formRect = form.getBoundingClientRect();
      const isOverlapping = formRect.left < navRect.right && formRect.right > navRect.left;

      setNavOverlap(isOverlapping);
    };
    const requestNavVisibilitySync = () => {
      if (!desktop.matches || !isSearchOpen || isNavSyncTicking) return;

      isNavSyncTicking = true;
      window.requestAnimationFrame(syncNavVisibility);
    };
    const clearPlaceholderTimer = () => {
      window.clearTimeout(placeholderTimer);
      placeholderTimer = null;
    };

    const stopTyping = () => {
      isTyping = false;
      clearPlaceholderTimer();
    };

    const typeNextFrame = () => {
      if (!isTyping || !isSearchOpen) return;

      const activeInput = desktop.matches ? input : mobileInput;
      if (activeInput?.value.trim()) {
        placeholderTimer = window.setTimeout(typeNextFrame, holdDelay);
        return;
      }

      const currentWord = words[wordIndex];

      if (!isErasing) {
        characterIndex += 1;
        setPlaceholder(currentWord.slice(0, characterIndex));

        if (characterIndex >= currentWord.length) {
          isErasing = true;
          placeholderTimer = window.setTimeout(typeNextFrame, holdDelay);
          return;
        }

        placeholderTimer = window.setTimeout(typeNextFrame, typeDelay);
        return;
      }

      characterIndex = Math.max(characterIndex - 1, 0);
      setPlaceholder(currentWord.slice(0, characterIndex));

      if (characterIndex === 0) {
        isErasing = false;
        wordIndex = (wordIndex + 1) % words.length;
        placeholderTimer = window.setTimeout(typeNextFrame, typeDelay);
        return;
      }

      placeholderTimer = window.setTimeout(typeNextFrame, eraseDelay);
    };

    const startTyping = () => {
      stopTyping();

      if (!words.length) {
        setPlaceholder('');
        return;
      }

      if (reducedMotion.matches || words.length < 2) {
        setPlaceholder(words[0]);
        return;
      }

      wordIndex = 0;
      characterIndex = 0;
      isErasing = false;
      isTyping = true;
      setPlaceholder('');
      typeNextFrame();
    };

    const openSearch = (focusInput = true) => {
      if (!desktop.matches || isSearchOpen) return;

      isSearchOpen = true;
      header.classList.add('is-desktop-search-open');
      syncProjectedNavVisibility();
      search.classList.add('is-search-open');
      toggle.setAttribute('aria-expanded', 'true');
      form.setAttribute('aria-hidden', 'false');
      input.removeAttribute('tabindex');

      window.setTimeout(() => {
        requestNavVisibilitySync();
        if (focusInput) {
          input.focus({ preventScroll: true });
        }
      }, 160);

      startTyping();
    };

    const closeSearch = (returnFocus = false) => {
      if (isAlwaysOpen) return;
      if (!isSearchOpen || input.value.trim()) return;

      isSearchOpen = false;
      isNavOverlapping = false;
      search.classList.remove('is-search-open');
      header.classList.remove('is-desktop-search-open');
      header.classList.remove('is-desktop-search-overlapping-nav');
      toggle.setAttribute('aria-expanded', 'false');
      form.setAttribute('aria-hidden', 'true');
      input.setAttribute('tabindex', '-1');
      input.blur();
      stopTyping();

      if (words.length) {
        setPlaceholder(words[0]);
      }

      if (returnFocus) {
        toggle.focus({ preventScroll: true });
      }
    };

    if (isAlwaysOpen) {
      isSearchOpen = true;
      header.classList.add('is-desktop-search-open');
      search.classList.add('is-search-open');
      toggle.setAttribute('aria-expanded', 'true');
      form.setAttribute('aria-hidden', 'false');
      input.removeAttribute('tabindex');
      startTyping();

      window.requestAnimationFrame(() => {
        syncProjectedNavVisibility();
        requestNavVisibilitySync();
      });
    }

    const handleDesktopChange = (event) => {
      if (!event.matches) {
        setNavOverlap(false);
        if (isSearchOpen) startTyping();
        return;
      }

      if (!isSearchOpen) return;

      startTyping();
      window.requestAnimationFrame(() => {
        syncProjectedNavVisibility();
        requestNavVisibilitySync();
      });
    };

    toggle.addEventListener('click', () => openSearch(true));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeSearch(true);
      }
    });

    document.addEventListener('click', (event) => {
      if (isSearchOpen && !search.contains(event.target)) {
        closeSearch(false);
      }
    });

    window.addEventListener('resize', requestNavVisibilitySync);
    desktop.addEventListener('change', handleDesktopChange);
  };

  const initDesktopMegaMenu = (header) => {
    const triggers = [...header.querySelectorAll('[data-mega-menu-trigger]')];
    const hoverRegion = header.querySelector('[data-mega-menu-hover-region]');
    const menus = [...header.querySelectorAll('[data-mega-menu]')];
    const dismissLinks = [...header.querySelectorAll('.site-header__nav-link:not([data-mega-menu-trigger])')];
    const desktop = window.matchMedia('(min-width: 1200px)');

    if (!triggers.length || !hoverRegion || !menus.length) return;

    const menuByTrigger = new Map();

    triggers.forEach((trigger) => {
      const menuId = trigger.getAttribute('aria-controls');
      const menu = menus.find((candidate) => candidate.id === menuId);

      if (menu) {
        menuByTrigger.set(trigger, menu);
      }
    });

    if (!menuByTrigger.size) return;

    let isOpen = false;
    let activeTrigger = null;
    let activeMenu = null;
    let closeTimer = null;
    let suppressFocusOpen = false;

    const clearCloseTimer = () => {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    const setExpandedTrigger = (triggerToExpand = null) => {
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', trigger === triggerToExpand ? 'true' : 'false');
      });
    };

    const openMenu = (trigger) => {
      if (!desktop.matches || suppressFocusOpen) return;

      const nextMenu = menuByTrigger.get(trigger);
      if (!nextMenu) return;

      clearCloseTimer();

      if (activeMenu && activeMenu !== nextMenu) {
        activeMenu.hidden = true;
      }

      activeTrigger = trigger;
      activeMenu = nextMenu;
      setExpandedTrigger(trigger);
      isOpen = true;
      activeMenu.hidden = false;
      header.classList.add('is-mega-menu-open');
    };

    const closeMenu = (returnFocus = false) => {
      clearCloseTimer();
      if (!isOpen) return;

      const triggerToFocus = activeTrigger;
      isOpen = false;
      activeTrigger = null;
      activeMenu = null;
      menus.forEach((menu) => {
        menu.hidden = true;
      });
      setExpandedTrigger();
      header.classList.remove('is-mega-menu-open');

      if (returnFocus && triggerToFocus) {
        suppressFocusOpen = true;
        triggerToFocus.focus({ preventScroll: true });
        window.requestAnimationFrame(() => {
          suppressFocusOpen = false;
        });
      }
    };

    const scheduleClose = () => {
      clearCloseTimer();
      closeTimer = window.setTimeout(() => closeMenu(), 180);
    };

    const isInsideDesktopMenu = (target) => {
      return target instanceof Node && (
        hoverRegion.contains(target)
        || menus.some((menu) => menu.contains(target))
      );
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => openMenu(trigger));
      trigger.addEventListener('focusin', () => openMenu(trigger));
      trigger.addEventListener('click', (event) => {
        if (!desktop.matches) return;

        const triggerUrl = trigger.getAttribute('href');
        if (triggerUrl !== '#' && triggerUrl !== '') return;

        event.preventDefault();
        openMenu(trigger);
      });
      trigger.addEventListener('focusout', (event) => {
        if (!isInsideDesktopMenu(event.relatedTarget)) {
          scheduleClose();
        }
      });
    });

    dismissLinks.forEach((link) => {
      link.addEventListener('pointerenter', () => closeMenu());
      link.addEventListener('focusin', () => closeMenu());
    });

    hoverRegion.addEventListener('pointerleave', scheduleClose);
    menus.forEach((menu) => {
      menu.addEventListener('pointerenter', clearCloseTimer);
      menu.addEventListener('pointerleave', scheduleClose);
      menu.addEventListener('focusin', clearCloseTimer);
      menu.addEventListener('focusout', (event) => {
        if (!isInsideDesktopMenu(event.relatedTarget)) {
          scheduleClose();
        }
      });
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) {
          window.setTimeout(() => closeMenu(), 0);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        closeMenu(true);
      }
    });

    desktop.addEventListener('change', (event) => {
      if (!event.matches) {
        closeMenu();
      }
    });
  };

  const initDockedHeader = (header) => {
    if (!header.classList.contains('header-wrapper--sticky')) return;

    let isTicking = false;
    let isDocked = null;

    const syncDockedState = () => {
      const shouldDock = window.scrollY > 0;

      if (shouldDock !== isDocked) {
        isDocked = shouldDock;
        header.classList.toggle('is-header-docked', isDocked);
        document.documentElement.classList.toggle('has-header-docked', isDocked);
      }

      isTicking = false;
    };

    const requestSync = () => {
      if (isTicking) return;

      isTicking = true;
      window.requestAnimationFrame(syncDockedState);
    };

    syncDockedState();
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync);
  };

  const initFooterTyping = (element) => {
    if (element.dataset.footerTypingInitialized === 'true') return;
    element.dataset.footerTypingInitialized = 'true';

    const words = [
      element.dataset.wordOne,
      element.dataset.wordTwo,
      element.dataset.wordThree,
    ].map((word) => (word || '').trim()).filter(Boolean);

    if (!words.length) return;

    const typeDelay = 90;
    const eraseDelay = 35;
    const holdDelay = 1000;
    let timer = null;
    let wordIndex = 0;
    let characterIndex = 0;
    let isErasing = false;
    let isTyping = false;

    const setText = (value) => {
      element.textContent = value;
    };

    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = null;
    };

    const stop = () => {
      isTyping = false;
      clearTimer();
    };

    const tick = () => {
      if (!isTyping) return;

      const currentWord = words[wordIndex];

      if (!isErasing) {
        characterIndex += 1;
        setText(currentWord.slice(0, characterIndex));

        if (characterIndex >= currentWord.length) {
          isErasing = true;
          timer = window.setTimeout(tick, holdDelay);
          return;
        }

        timer = window.setTimeout(tick, typeDelay);
        return;
      }

      characterIndex = Math.max(characterIndex - 1, 0);
      setText(currentWord.slice(0, characterIndex));

      if (characterIndex === 0) {
        isErasing = false;
        wordIndex = (wordIndex + 1) % words.length;
        timer = window.setTimeout(tick, typeDelay);
        return;
      }

      timer = window.setTimeout(tick, eraseDelay);
    };

    const start = () => {
      if (isTyping) return;

      stop();

      if (reducedMotion.matches || words.length < 2) {
        setText(words[0]);
        return;
      }

      isTyping = true;
      wordIndex = 0;
      characterIndex = 0;
      isErasing = false;
      setText('');
      tick();
    };

    if (!('IntersectionObserver' in window)) {
      start();
      element.footerTypingCleanup = () => {
        stop();
        delete element.dataset.footerTypingInitialized;
      };
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          start();
        } else {
          stop();
        }
      });
    }, { rootMargin: '120px 0px' });

    observer.observe(element);
    element.footerTypingCleanup = () => {
      stop();
      observer?.disconnect();
      delete element.dataset.footerTypingInitialized;
    };
  };

  headers.forEach((header) => {
    initMobileMenu(header);
    initDesktopSearch(header);
    initDesktopMegaMenu(header);
    initDockedHeader(header);
  });

  document.querySelectorAll('[data-footer-typing]').forEach(initFooterTyping);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-footer-typing]').forEach(initFooterTyping);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-footer-typing]').forEach((element) => {
      element.footerTypingCleanup?.();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableDomReadyMotion, { once: true });
  } else {
    enableDomReadyMotion();
  }
})();
